import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';
import { addYears, format } from 'date-fns';
import { AlertTriangle, Info, X } from 'lucide-react';
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Badge } from '../../ui/badge';
import { DatePicker } from '../../ui/date-picker';
import { DocumentRichTextEnhanced } from '../../documents/form/inputs/DocumentRichTextEnhanced';
import LoadingRing from '../../ui-components/LoadingRing';
import { useToast } from '../../../hooks/use-toast';
import { cn } from '../../../lib/utils';
import { getPolicy } from '../../../services/api/policyService';
import type { ParsedPolicy } from './DocxPolicyUpload';

interface FormValues {
    title: string;
    effectiveDate: string;
    expiryDate: string;
    tags: string[];
}

interface PolicyEditDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    policy: ParsedPolicy | null;
    onSave: (policy: ParsedPolicy) => Promise<void>;
}

const addOneYear = (dateStr: string): string => {
    if (!dateStr) return '';
    const [y, m, d] = dateStr.split('-').map(Number);
    return format(addYears(new Date(y, m - 1, d), 1), 'yyyy-MM-dd');
};

export default function PolicyEditDialog({
    open,
    onOpenChange,
    policy,
    onSave,
}: PolicyEditDialogProps) {
    const { toast } = useToast();
    const { t } = useTranslation();
    const [editedHtml, setEditedHtml] = useState('');
    const [htmlReady, setHtmlReady] = useState(false);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [tagInput, setTagInput] = useState('');

    const policyId = policy?._id;
    const isNew = !policyId;
    const initialHtml = policy?.html ?? '';
    const needsFetch = Boolean(open && policy && !initialHtml && policyId);

    const {
        register,
        control,
        handleSubmit,
        formState: { errors, isValid },
        setValue,
        watch,
        reset,
    } = useForm<FormValues>({
        mode: 'onChange',
        defaultValues: {
            title: '',
            effectiveDate: '',
            expiryDate: '',
            tags: [],
        },
    });

    useEffect(() => {
        if (!open || !policy) {
            setEditedHtml('');
            setHtmlReady(false);
            setLoading(false);
            setTagInput('');
            return;
        }

        reset({
            title: policy.title,
            effectiveDate: policy.effectiveDate,
            expiryDate: policy.expiryDate,
            tags: [...policy.tags],
        });

        if (!needsFetch || !policyId) {
            setEditedHtml(initialHtml);
            setHtmlReady(true);
            return;
        }

        let cancelled = false;
        const loadContent = async () => {
            setLoading(true);
            setEditedHtml('');
            try {
                const populated = await getPolicy(policyId);
                if (cancelled) return;
                const html =
                    (populated.document_id?.data?.policyData as string) ?? '';
                setEditedHtml(html);
                setHtmlReady(true);
            } catch (err) {
                if (cancelled) return;
                setEditedHtml('');
                toast({
                    title: t('facilities.policyEditDialog.loadFailed'),
                    description:
                        err instanceof Error
                            ? err.message
                            : t('facilities.policyEditDialog.loadFailedDesc'),
                    variant: 'destructive',
                });
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        loadContent();

        return () => {
            cancelled = true;
        };
    }, [open, needsFetch, policyId, initialHtml, policy, reset, toast, t]);

    const watchedTags = watch('tags');

    const addTag = () => {
        const trimmed = tagInput.trim();
        if (!trimmed) return;
        if (
            watchedTags.some((existing) => existing.toLowerCase() === trimmed.toLowerCase())
        ) {
            setTagInput('');
            return;
        }
        setValue('tags', [...watchedTags, trimmed]);
        setTagInput('');
    };

    const removeTag = (tag: string) => {
        setValue(
            'tags',
            watchedTags.filter((existing) => existing !== tag)
        );
    };

    const onSubmit = handleSubmit(async (values) => {
        if (!policy) return;
        setSaving(true);
        try {
            await onSave({
                ...policy,
                title: values.title.trim(),
                effectiveDate: values.effectiveDate,
                expiryDate: values.expiryDate,
                tags: values.tags,
                html: editedHtml,
            });
            onOpenChange(false);
        } catch (err) {
            toast({
                title: t('facilities.policyEditDialog.saveFailed'),
                description:
                    err instanceof Error
                        ? err.message
                        : t('facilities.policyEditDialog.saveFailedDesc'),
                variant: 'destructive',
            });
        } finally {
            setSaving(false);
        }
    });

    const warnings = policy?.warnings ?? [];

    return (
        <Dialog
            open={open}
            onOpenChange={(next) => {
                if (saving) return;
                onOpenChange(next);
            }}
        >
            <DialogContent className="flex max-h-[90vh] max-w-4xl flex-col overflow-hidden">
                <DialogHeader>
                    <DialogTitle className="truncate pr-8">
                        {isNew
                            ? t('facilities.policyEditDialog.importTitle', { fileName: policy?.fileName })
                            : t('facilities.policyEditDialog.previewTitle')}
                    </DialogTitle>
                </DialogHeader>

                {loading || !htmlReady ? (
                    <div className="flex h-64 items-center justify-center">
                        <LoadingRing size="large" />
                    </div>
                ) : policy ? (
                    <form
                        onSubmit={onSubmit}
                        className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]"
                    >
                        <div className="space-y-1.5">
                            <Label htmlFor="policy-title">
                                {t('facilities.policyEditDialog.title')}{' '}
                                <span className="text-destructive">*</span>
                            </Label>
                            <Input
                                id="policy-title"
                                {...register('title', { required: true })}
                                className={cn(
                                    'h-9 text-sm',
                                    errors.title && 'border-destructive'
                                )}
                            />
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1.5">
                                <Label htmlFor="policy-effective-date">
                                    {t('facilities.policyEditDialog.effectiveDate')}{' '}
                                    <span className="text-destructive">*</span>
                                </Label>
                                <Controller
                                    control={control}
                                    name="effectiveDate"
                                    rules={{ required: true }}
                                    render={({ field }) => (
                                        <DatePicker
                                            value={field.value}
                                            onChange={(next) => {
                                                field.onChange(next);
                                                if (
                                                    isNew &&
                                                    !watch('expiryDate') &&
                                                    next
                                                ) {
                                                    setValue(
                                                        'expiryDate',
                                                        addOneYear(next),
                                                        {
                                                            shouldValidate:
                                                                true,
                                                        }
                                                    );
                                                }
                                            }}
                                            placeholder={t('facilities.policyEditDialog.pickDate')}
                                            className={cn(
                                                'h-9 text-sm',
                                                errors.effectiveDate &&
                                                    'border-destructive'
                                            )}
                                        />
                                    )}
                                />
                            </div>
                            <div className="space-y-1.5">
                                <Label htmlFor="policy-expiry-date">
                                    {t('facilities.policyEditDialog.expiryDate')}{' '}
                                    <span className="text-destructive">*</span>
                                </Label>
                                <Controller
                                    control={control}
                                    name="expiryDate"
                                    rules={{ required: true }}
                                    render={({ field }) => (
                                        <DatePicker
                                            value={field.value}
                                            onChange={field.onChange}
                                            placeholder={t('facilities.policyEditDialog.pickDate')}
                                            className={cn(
                                                'h-9 text-sm',
                                                errors.expiryDate &&
                                                    'border-destructive'
                                            )}
                                        />
                                    )}
                                />
                            </div>
                        </div>

                        <div className="space-y-1.5">
                            <Label htmlFor="policy-tags">{t('facilities.policyEditDialog.tags')}</Label>
                            <Input
                                id="policy-tags"
                                value={tagInput}
                                onChange={(e) => setTagInput(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        addTag();
                                    }
                                }}
                                placeholder={t('facilities.policyEditDialog.tagPlaceholder')}
                                className="h-9 text-sm"
                            />
                            {watchedTags.length > 0 && (
                                <div className="flex flex-wrap gap-1.5 pt-1">
                                    {watchedTags.map((tag) => (
                                        <Badge
                                            key={tag}
                                            variant="secondary"
                                            className="h-6 gap-1 border-0 bg-zinc-200 pr-1 text-xs font-normal text-zinc-800 hover:bg-zinc-200"
                                        >
                                            {tag}
                                            <button
                                                type="button"
                                                onClick={() => removeTag(tag)}
                                                className="rounded-full p-0.5 hover:bg-muted-foreground/20"
                                                aria-label={t('facilities.policyEditDialog.removeTag', { tag })}
                                            >
                                                <X
                                                    className="h-2.5 w-2.5"
                                                    aria-hidden="true"
                                                />
                                            </button>
                                        </Badge>
                                    ))}
                                </div>
                            )}
                        </div>

                        <div className="space-y-1.5">
                            <Label>{t('facilities.policyEditDialog.content')}</Label>
                            <div className="flex items-start gap-2 rounded-md border border-amber-500/30 bg-amber-500/10 p-3 text-sm text-amber-400">
                                <Info
                                    className="mt-0.5 h-4 w-4 shrink-0"
                                    aria-hidden="true"
                                />
                                <span>
                                    {t('facilities.policyEditDialog.contentHint')}
                                </span>
                            </div>
                            <DocumentRichTextEnhanced
                                value={editedHtml}
                                onChange={setEditedHtml}
                            />
                        </div>

                        {warnings.length > 0 && (
                            <div className="rounded-md border border-amber-500/30 bg-amber-500/10 p-3">
                                <p className="flex items-center gap-1.5 text-xs font-medium text-amber-400">
                                    <AlertTriangle
                                        className="h-3.5 w-3.5"
                                        aria-hidden="true"
                                    />
                                    {t('facilities.policyEditDialog.formattingWarnings')}
                                </p>
                                <ul className="mt-1.5 space-y-0.5 text-xs text-muted-foreground">
                                    {warnings.map((w, i) => (
                                        <li key={i}>• {w}</li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </form>
                ) : null}

                {policy && !loading && (
                    <DialogFooter className="gap-2 sm:gap-2">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => onOpenChange(false)}
                            disabled={saving}
                        >
                            {t('facilities.policyEditDialog.cancel')}
                        </Button>
                        <Button
                            type="button"
                            onClick={onSubmit}
                            disabled={!isValid || saving}
                        >
                            {saving ? t('facilities.policyEditDialog.saving') : t('facilities.policyEditDialog.save')}
                        </Button>
                    </DialogFooter>
                )}
            </DialogContent>
        </Dialog>
    );
}
